<template>
  <div v-if="project">
    <b-card
      class="mb-1"
      no-body
    >
      <b-card-header>
        <div class="d-flex justify-content-start">
          <b-avatar
            :variant="`light-${project.isChildren ? 'warning' :'success'}`"
            rounded
          >
            <feather-icon
              size="2x"
              :icon="project.isChildren ? 'UserIcon' :'MapPinIcon'"
            />
          </b-avatar>
          <div class="d-flex flex-column ml-1">
            <h4 class="mb-0">
              {{ project.projectName || (pid=== 0 ? $t('projects.add-new-' + (project.isChildren ? 'sad' : 'projects')) : null) }}
            </h4>
            <span class="card-text">
              {{ $t('projects.' + (project.isChildren ? 'sad' : 'project')) }}
              <b-badge
                v-show="project.infos.status"
                pill
                :variant="`light-${resolveStatusVariant(project.infos.status)}`"
                class="text-capitalize"
              >
                {{ project.infos.status }}
              </b-badge>
            </span>
          </div>
        </div>
        <b-button-group>
          <b-button
            v-if="!isEditing"
            class="btn-icon"
            variant="outline-primary"
            @click="createDocX()"
          >
            <feather-icon
              class="text-nowrap d-block d-sm-none"
              icon="FileTextIcon"
            />
            <span class="text-nowrap d-none d-sm-block">{{ $t('attachments.create-docx') }}</span>
          </b-button>
          <b-button
            v-if="pid !== 0 && isUserAllowWrite"
            class="btn-icon"
            variant="outline-primary"
            @click="edit()"
          >
            <feather-icon
              class="text-nowrap d-block d-sm-none"
              :icon="isEditing ? 'XIcon' : 'EditIcon'"
            />
            <span class="text-nowrap d-none d-sm-block">{{ $t('common.' + (!isEditing ? 'edit' : 'close')) }}</span>
          </b-button>

          <b-button
            v-if="pid !== 0 && isUserAllowWrite && !project.isChildren"
            class="btn-icon"
            variant="outline-primary"
            @click="duplicate()"
          >
            <feather-icon
              class="text-nowrap d-block d-sm-none"
              icon="RefreshCcwIcon"
            />
            <span class="text-nowrap d-none d-sm-block">{{ $t('projects.duplicate') }}</span>
          </b-button>

          <b-button
            v-if="pid !== 0 && isUserAllowWrite && isUserAllowDelete && !isEditing"
            class="btn-icon"
            variant="outline-primary"
            @click="deletee()"
          >
            <feather-icon
              class="text-nowrap d-block d-sm-none"
              icon="TrashIcon"
            />
            <span class="text-nowrap d-none d-sm-block">{{ $t('common.delete') }}</span>
          </b-button>

          <b-button
            v-if="isUserAllowWrite"
            class="btn-icon"
            variant="outline-primary"
            :disabled="!somethingChanged || !project.projectName"
            @click="submit()"
          >
            <feather-icon
              class="text-nowrap d-block d-sm-none"
              icon="SaveIcon"
            />
            <span class="text-nowrap d-none d-sm-block">{{ $t('common.save') }}</span>
          </b-button>
        </b-button-group>
      </b-card-header>
    </b-card>

    <projects-edit
      v-if="isEditing"
      v-model="project"
      :refaid="refaid"
      :lookup-options="lookupOptions"
    />
    <projects-view-card
      v-if="!isEditing"
      v-model="project"
      :refaid="refaid"
      :tags-options="tagsOptions"
      :tags-colors="tagsColors"
      @on-submit="submit()"
    />

    <b-row
      v-if="pid !== 0 && !project.isChildren"
      class="match-height"
    >
      <b-col
        md="6"
        cols="12"
      >
        <donations-list
          ref="giversListTable"
          :pid="pid"
          :is-linked="project.isLinked"
          :linked-area="selectedArea"
          :is-giver-mode="true"
        />
      </b-col>
      <b-col
        md="6"
        cols="12"
      >
        <projects-view-tasks
          :tasks="project.tasks"
          :wizard-tasks="wizardTasks"
          @on-submit="submit()"
        />
      </b-col>
    </b-row>
    <b-row v-if="pid !== 0 && project.isLinked">
      <b-col
        cols="12"
        md="6"
      >
        <b-form-group>
          <label for="linkedAreas"><strong>{{ $t('projects.linked') }}</strong>
          </label>
          <b-form-radio-group
            v-model="selectedArea"
            :options="linkedAreas"
            name="linkedAreas"
            class="d-inline-block ml-2"
            value-field="aid"
            text-field="areaName"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <donations-list
      v-if="pid !== 0"
      ref="donationsListTable"
      :pid="pid"
      :is-children="project.isChildren"
      :is-linked="project.isLinked"
      :linked-area="selectedArea"
      @on-reloaded="onDonationsListReloaded"
    />
    <contributors-list
      v-if="pid !== 0"
      ref="contributorsListTable"
      :cpid="pid"
      :is-linked="project.isLinked"
      :linked-area="selectedArea"
    />

    <docs-modal
      ref="docsModal"
      :table="project.isChildren ? 'sad' : 'projects'"
      :refaid="refaid"
      :rename="project.projectName"
      :templates="templatesOptions"
    />
  </div>

</template>

<script>
import {
  BCard, BCardHeader, BButton, BButtonGroup, BFormGroup, BFormRadioGroup, BBadge, BAvatar, BRow, BCol,
} from 'bootstrap-vue'
import axios from '@axios'
import { ref } from '@vue/composition-api'
// eslint-disable-next-line import/no-cycle
import router from '@/vue.router'
import DonationsList from '@/views/donations/DonationsList.vue'
import ProjectsEdit from '@/views/projects/ProjectsEdit.vue'
import ProjectsViewCard from '@/views/projects/ProjectsViewCard.vue'
import ProjectsViewTasks from '@/views/projects/ProjectsViewTasks.vue'
import ContributorsList from '@/views/contributors/ContributorsList.vue'
import authService from '@/vue.auth.service'
import lookupsService from '@/vue.lookups.service'
import DocsModal from '@/views/layouts/DocsModal.vue'
import {
  avatarText, resolveAvatarVariant, resolveStatusVariant, clone, swalSaveOptions, swalCloseOptions, toastSuccessOptions, decode, toastErrorOptions, swalDeleteOptions,
} from '../layouts/layouts.utils'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BButton,
    BButtonGroup,
    BFormGroup,
    BFormRadioGroup,
    BAvatar,
    BBadge,
    DonationsList,
    ProjectsEdit,
    ProjectsViewCard,
    ProjectsViewTasks,
    ContributorsList,
    DocsModal,
  },
  data() {
    return {
      isUserAllowWrite: authService.isUserInRole('write'),
      isUserAllowDelete: authService.isUserInRole('delete'),
    }
  },
  async mounted() {
    const currentArea = await authService.currentArea()
    const isMultiArea = await authService.isMultiArea()
    this.refaid = currentArea.aid
    this.selectedArea = (isMultiArea ? currentArea.aid : null)
    this.linkedAreas = (isMultiArea ? await authService.getAllowedAreas() : [])
    this.templatesOptions = currentArea.settings.templates
    if (this.templatesOptions) this.templatesOptions.sort((a, b) => (a.label > b.label ? 1 : -1))
    this.wizardTasks = currentArea.settings.defaults.tasks
    this.tagsOptions = await lookupsService.getMaps('projects.tags', currentArea.aid, currentArea.settings.obsoletes)
    this.tagsColors = currentArea.settings.tags
    const options = {
      missionOptions: await lookupsService.getMaps('projects.missionName', currentArea.aid),
      tribooOptions: await lookupsService.getMaps('projects.triboo', currentArea.aid),
      nationsOptions: await lookupsService.getMaps('nations', currentArea.aid),
      statusOptions: await lookupsService.getMaps('projects.status', currentArea.aid),
      projectTypeOptions: await lookupsService.getMaps('projects.projectType', currentArea.aid),
    }
    this.lookupOptions = options
    await this.refetchData()
    this.somethingChanged = false
    // eslint-disable-next-line no-underscore-dangle
    if (!this._events.changedArea) {
      this.$root.$on('changedArea', () => {
        this.$root.$off('changedArea')
        this.$router.push({ name: 'list-projects' })
      })
    }
    this.$watch('project', () => {
      this.somethingChanged = true
    }, { deep: true })
  },
  beforeDestroy() {
    this.$root.$off('changedArea')
  },
  methods: {
    edit() {
      if (this.isEditing && this.somethingChanged) {
        this.$swal(swalSaveOptions).then(result => {
          if (result.value === true) {
            this.submit()
          } else if (result.value === false) {
            this.project = clone(this.original)
            setTimeout(this.reset, 500)
          }
        })
      } else this.isEditing = (!this.isEditing)
    },
    deletee() {
      this.$swal(swalDeleteOptions).then(async result => {
        if (result.value === true) {
          try {
            const resd = await axios.get('/api/record/projects', { params: { aid: this.refaid, rid: this.pid } })
            resd.pleaseDelete = true
            await axios.post('/api/record/projects',
              JSON.stringify(resd),
              { params: { aid: this.refaid, rid: this.pid } })
            this.$toast(toastSuccessOptions, { position: 'top-center' })
            this.$router.replace({ name: 'list-projects' })
          } catch (error) {
            const message = toastErrorOptions
            if (error.response) message.props.text = decode(error.response.data.error)
            this.$toast(message, { position: 'top-center' })
          }
        }
      })
    },
    createDocX() {
      const { selected } = this.$refs.donationsListTable
      this.$refs.docsModal.show({ pid: this.pid, dids: selected }, async () => {
        const response = await axios.get('/api/record/projects', { params: { aid: this.refaid, rid: this.pid } })
        this.project.lastUpdate = response.data.lastUpdate
        this.project.editor = response.data.editor
        this.project.attachments = response.data.attachments
        setTimeout(this.reset, 500)
      })
    },
    onDonationsListReloaded() {
      const newDonation = (router.currentRoute.query.newDonation && router.currentRoute.query.newDonation.toString().toLowerCase() === 'true')
      if (newDonation) {
        this.$refs.donationsListTable.addNew()
        this.$refs.donationsListTable.scrollToNew()
      }
    },
    duplicate() {
      this.$router.replace({ name: 'view-projects', params: { id: 0, dup: this.pid } })
    },
    async submit() {
      try {
        const response = await axios.post('/api/record/projects',
          JSON.stringify(this.project),
          { params: { aid: this.refaid, rid: this.pid } })
        lookupsService.refreshMaps(this.project.infos.missionName, 'projects.missionName', this.refaid, this.lookupOptions.missionOptions)
        lookupsService.refreshMaps(this.project.infos.nation, 'nations', this.refaid, this.lookupOptions.nationsOptions)
        lookupsService.refreshMaps(this.project.tags, 'projects.tags', this.refaid)
        lookupsService.refreshMaps(this.project.infos.status, 'projects.status', this.refaid, this.lookupOptions.statusOptions)
        lookupsService.refreshMaps(this.project.infos.projectType, 'projects.projectType', this.refaid, this.lookupOptions.projectTypeOptions)

        if (this.pid === 0) {
          this.$router.replace({ name: 'view-projects', params: { id: response.data.rid } })
          await this.refetchData()
        } else {
          if (this.project.links && this.project.links.areas.length === 0) {
            await this.refetchData()
          } else {
            this.project.editor = response.data.editor
            this.project.lastUpdate = response.data.lastUpdate
            this.original = clone(this.project)
          }
          this.$toast(toastSuccessOptions, { position: 'top-center' })
        }
      } catch (error) {
        const message = toastErrorOptions
        if (error.response) message.props.text = decode(error.response.data.error)
        this.$toast(message, { position: 'top-center' })
        await this.refetchData()
      }
      setTimeout(this.reset, 500)
    },
  },
  setup() {
    const refaid = ref(null)
    const pid = ref(null)
    const project = ref(null)
    const original = ref(null)
    const somethingChanged = ref(false)
    const isEditing = ref(false)
    const tagsOptions = ref([])
    const tagsColors = ref([])
    const linkedAreas = ref([])
    const lookupOptions = ref({})
    const templatesOptions = ref([])
    const wizardTasks = ref([])
    const selectedArea = ref(null)

    const reset = () => {
      isEditing.value = false
      somethingChanged.value = false
    }
    const refetchData = async () => {
      pid.value = parseInt(router.currentRoute.params.id, 0)
      if (pid.value === 0) {
        const dup = parseInt(router.currentRoute.params.dup, 0)
        const zeroTasks = (wizardTasks.value && wizardTasks.value.length > 0 ? wizardTasks.value.map(w => ({ title: w, status: 'todo' })) : [])
        if (dup && dup > 0) {
          const source = (await axios.get('/api/record/projects', { params: { aid: refaid.value, rid: dup } })).data
          source.projectName += ' (2)'
          source.pid = 0
          source.tasks = zeroTasks
          source.infos.notes = null
          source.links = null
          project.value = source
        } else {
          project.value = {
            projectName: null,
            isChildren: (router.currentRoute.query.isChildren && router.currentRoute.query.isChildren.toString().toLowerCase() === 'true'),
            infos: {},
            tasks: zeroTasks,
          }
        }
        original.value = null
        isEditing.value = true
      } else if (pid.value) {
        const response = await axios.get('/api/record/projects', { params: { aid: refaid.value, rid: pid.value } })
        if (!response.data.infos) response.data.infos = {}
        if (!response.data.tasks) response.data.tasks = []
        project.value = response.data
        if (!project.value.tasks || project.value.tasks === null) project.value.tasks = []
        original.value = clone(project.value)
      }
    }

    return {
      refaid,
      pid,
      project,
      original,
      isEditing,
      somethingChanged,
      templatesOptions,
      wizardTasks,
      tagsOptions,
      tagsColors,
      selectedArea,
      linkedAreas,
      lookupOptions,
      reset,
      refetchData,
      avatarText,
      resolveAvatarVariant,
      resolveStatusVariant,
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (this.somethingChanged || (this.$refs.donationsListTable && this.$refs.donationsListTable.somethingChanged)) {
      const result = await this.$swal(swalCloseOptions)
      if (!result.value) {
        next(false)
        return
      }
    }
    next()
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>
